import React, { useEffect, useState } from 'react';
import {
    CreateButton,
    Datagrid,
    EditButton,
    FunctionField,
    List,
    TopToolbar,
    useAuthProvider,
    useListContext,
    useStore
} from 'react-admin';
import { Box, Container, Tab, Tabs } from '@mui/material';
import { ReactComponent as EditIcon } from 'src/assets/pencil-alt.svg';
import { listContainerStyles } from 'src/styles';
import UnknownAvatar from 'src/components/common/UnknownAvatar';
import ShsSearchInput from 'src/components/common/Fields/ShsSearchInput';
import ShsSelectInput from 'src/components/common/Fields/ShsSelectInput';
import WithRole from 'src/components/common/WithRole';
import ShsTitle from 'src/components/common/ShsTitle';
import ShsAutoCompleteWithLoad from 'src/components/common/Fields/ShsAutoCompleteWithLoad';
import StatusBadge from 'src/components/common/StatusBadge';
import { NoValuePlaceholder } from 'src/constants/children/form';
import { EmployeesManager } from 'src/managers/Employees/EmployeesManager';
import { canEditEmployeeRoles } from './EmployeeConstants';
import { AdminAndSecretariat } from 'src/constants/roles';
import { FILTER_EMPTY_VALUE, STATUS, UserRoleLabel } from 'src/constants';
import { UserRole } from 'src/auth/types';
import { EmployeeCenterDetailsType, EmployeeType } from './types';
import { AddCircleOutline } from '@mui/icons-material';

/**
 * Gets filters based on the provided parameters
 * @param { boolean } showEducationCenterFilter - indicates whether to include the education center filter
 */
const getFilters = (showEducationCenterFilter: boolean) => {
    const filters = [
        <ShsSearchInput key="1" source="keyword" label="Знайти працівника" sx={{ minWidth: '280px' }} alwaysOn />
    ];
    if (showEducationCenterFilter) {
        filters.push(
            <ShsAutoCompleteWithLoad
                key="2"
                source="educationCenterId"
                pageUrl="centers"
                filters={{ isActive: true }}
                emptyValue={FILTER_EMPTY_VALUE}
                emptyText="Усі"
                alwaysOn
                keyField="id"
                textField="shortName"
                resultLimit={100}
                label="Освітній центр"
                sx={{ minWidth: '280px' }}
            />
        );
    }
    filters.push(
        <ShsSelectInput
            key="3"
            source="employmentType"
            label="Працевлаштування"
            alwaysOn
            emptyValue={FILTER_EMPTY_VALUE}
            emptyText="Усі"
            choices={[
                { name: 'Штатне', id: 'STAFF' },
                { name: 'ЦПХ', id: 'CONTRACT' }
            ]}
            sx={{ minWidth: '135px' }}
        />
    );
    filters.push(
        <ShsSelectInput
            key="4"
            source="centersInvolvementFilter"
            label="Задіяність в центрах"
            alwaysOn
            emptyValue={FILTER_EMPTY_VALUE}
            emptyText="Усі"
            choices={[
                { name: 'В одному центрі', id: 1 },
                { name: 'В декількох центрах', id: 2 }
            ]}
            sx={{ minWidth: '135px' }}
        />
    );

    return filters;
};

/**
 * Renders the toolbar with actions based on the user's role
 * Displays Add Employee button if the user has the appropriate role
 */
const ToolbarActions = () => {
    return (
        <TopToolbar>
            <WithRole roles={canEditEmployeeRoles}>
                <CreateButton label="Додати працівника" variant="contained" icon={<AddCircleOutline />} />
            </WithRole>
        </TopToolbar>
    );
};

/**
 * Keeps consistent employee row without changing the order of employeeCenters
 * @param { EmployeeCenterDetailsType[] } [centers]
 * @returns { EmployeeCenterDetailsType[] }
 */
const employeeCenterSorter = EmployeesManager.employeeCenterSorter;

const getSubjects = (centers?: EmployeeCenterDetailsType[]): EmployeeCenterDetailsType[] => {
    if (!centers) {
        return [];
    }
    return employeeCenterSorter(centers).filter(({ subjectNames }) => !!subjectNames?.length);
};

/**
 * Grid component that renders a list and optionally notifies about total changes
 */
const Grid: React.FC<{ onTotalChanged?: (total: number) => void }> = ({ onTotalChanged }) => {
    const { total } = useListContext();

    useEffect(() => onTotalChanged?.(total), [total]);

    return (
        <Datagrid bulkActionButtons={false} rowClick="show">
            <FunctionField
                sortBy="surname"
                label="Прізвище, ім’я та по батькові"
                className="bold"
                render={({ surname, name, patronymic }: EmployeeType) => (
                    <Box display="flex" flexDirection="row" alignItems="center">
                        <UnknownAvatar
                            lastName={surname}
                            firstName={name}
                            size="small"
                            style={{ marginRight: '12px' }}
                        />
                        <Box>{`${surname} ${name} ${patronymic || ''}`}</Box>
                    </Box>
                )}
            />

            <FunctionField
                label="Працевлаштування"
                render={({ employeeCenters }: EmployeeType) => {
                    const employments = employeeCenterSorter(employeeCenters).map((center) =>
                        [center.staffMember && 'Штатне', center.civilLawNatureContract && 'ЦПХ']
                            .filter((v) => !!v)
                            .join(', ')
                    );

                    return employments.map((name, index) => <Box key={index}>{name}</Box>);
                }}
            />

            <FunctionField
                label="Освітні центри"
                render={({ employeeCenters }: EmployeeType) =>
                    employeeCenterSorter(employeeCenters)?.map((center, index) => (
                        <Box key={index}>{center.shortName}</Box>
                    ))
                }
            />
            <FunctionField
                label="Роль"
                render={({ employeeCenters }: EmployeeType) =>
                    employeeCenterSorter(employeeCenters)?.map((center, index) => (
                        <Box key={index}>
                            {center.roles?.length && center.roles?.length > 0
                                ? center.roles.map((role) => UserRoleLabel[role] || '').join(', ')
                                : ''}
                        </Box>
                    ))
                }
            />
            <FunctionField
                label="Предмет"
                render={({ employeeCenters }: EmployeeType) => {
                    const subjects = getSubjects(employeeCenters);
                    return subjects.length ? (
                        subjects.map(({ subjectNames }, index) => (
                            <Box key={index}>{subjectNames.map((subject) => subject.name).join(', ')}</Box>
                        ))
                    ) : (
                        <Box>{NoValuePlaceholder}</Box>
                    );
                }}
            />
            <FunctionField
                label="Навантаження"
                className="bold"
                render={({ employeeCenters }: EmployeeType) =>
                    employeeCenterSorter(employeeCenters)?.map(({ staffLoadPerWeek, contractLoadPerWeek }, index) => (
                        <Box key={index}>{staffLoadPerWeek + contractLoadPerWeek + ' год/тижд'}</Box>
                    ))
                }
            />
            <FunctionField
                label="Статус"
                render={(employee: EmployeeType) => {
                    // const fired = !employee.active && !employee.restricted;

                    const fired = employee.employeeCenters?.reduce(
                        (prevVal, currentEmp) =>
                            prevVal && !!currentEmp.dateOfEnding && new Date(currentEmp.dateOfEnding) <= new Date(),
                        true
                    );
                    return (
                        <StatusBadge
                            status={
                                fired
                                    ? STATUS.INACTIVE
                                    : employee.active && !employee.restricted
                                      ? STATUS.ACTIVE
                                      : STATUS.SUSPENDED
                            }
                        />
                    );
                }}
            />
            <WithRole roles={[...canEditEmployeeRoles, UserRole.SECRETARIAT]}>
                <EditButton icon={<EditIcon />} label="" />
            </WithRole>
        </Datagrid>
    );
};

/**
 * Manages and displays list of employees
 * Includes tabbed navigation for active and inactive employees and updates the total count of employees
 */
const EmployeeList: React.FC = () => {
    const [activeTab, setActiveTab] = useStore<STATUS.ACTIVE | STATUS.INACTIVE>('employeeListTab', STATUS.ACTIVE);
    const [total, setTotal] = useState<number | undefined>(0);
    const authProvider = useAuthProvider();

    return (
        <Container sx={listContainerStyles}>
            <ShsTitle title="Працівники" found={total} foundTitle="Загалом" />
            <List<EmployeeType>
                resource="employees"
                exporter={false}
                empty={false}
                actions={<ToolbarActions />}
                filters={getFilters(authProvider?.containsRoles(AdminAndSecretariat))}
                filterDefaultValues={{
                    employmentType: FILTER_EMPTY_VALUE,
                    educationCenterId: FILTER_EMPTY_VALUE,
                    centersInvolvementFilter: FILTER_EMPTY_VALUE
                }}
                filter={activeTab == STATUS.ACTIVE ? { hasActiveEmployment: true } : { hasActiveEmployment: false }}>
                <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
                    <Tab value={STATUS.ACTIVE} label="Зараз працюють" />
                    <Tab value={STATUS.INACTIVE} label="Завершили працю" />
                </Tabs>
                <Grid onTotalChanged={(total) => setTotal(total)} />
            </List>
        </Container>
    );
};

export default EmployeeList;
